/**************************************************************************************************
 *
 *  @file ./decorator-serializable.ts
 *
 *  @type @Serializable({
 *            isSingleton?: boolean = false,
 *            storePrefix?: string = ``,
 *            underlyingData?: {
 *                type: Function & {name: string};
 *                idField?: string;
 *                deleteEventName: string
 *            }
 *        })
 *
 *  Class decorator that sets class up for serialization, local storage in
 *  localforage, retrieval on page load, preservation (or elimination) of
 *  stored values on logout. This includes adding setters for all @storeLocally
 *  marked props (e.g. creates setter "setName" for property "name")
 *
 *  Makes all serialization/storage-relatedproperty decorators work
 *
 *  @param {boolean} isSingleton If true, don't add an instance data value to
 *                               the storage key
 *  @param {string} filename? {OPTIONAL} Originating filename to show in logs
 *  @param {object} underlyingData {OPTIONAL} References to backing data for store,
 *         |        to auto-delete store if the instance it relies on is deleted
 *         |        (Params below)
 *         |-> @param {Class} type Data class the store relies on (e.g. Record)
 *         |-> @param {string} idField Treat an instance of given "type" as
 *         |                           matching if this field is equal to its
 *         |                           value on the instance
 *         \-> @param {string} deleteEventName Name of event emitted on
 *                                             underlying data deletion
 *
 *  @example Usage:
 *
 *      class CreateNewRecordStoreSetters extends BaseStore {
 *          [key: string]: any;
 *          setFirstName?:     (val: string)           => void;
 *          setLastName?:      (val: string)           => void;
 *          setBirthdate?:     (val: moment.Moment)    => void;
 *      }
 *
 *      @Serializable({
 *          isSingleton: false,
 *          filename: `create-new-record-store`, // <- For logging
 *          underlyingData?: {
 *              type: Record;
 *              idField?: `recordId`;
 *              deleteEventName: "ev_DELETE_RECORD"
 *          }
 *      })
 *      export class CreateNewRecordStore extends CreateNewRecordStoreSetters {
 *          /...
 *      }
 */

/**************************************** PROJECT MODULES *****************************************/
import {modelInitStatusStore} from '../../models/app/model-init-status-store';

import {
    addInstanceRefPropValToRefPropValList,
    augmentNewConstructorWithOrig,
    buildDbStorageKey,
    getStoredPropsList,
    getReferenceProp,
    getClassData,
    isInitForStorage,
    incrementClassInstanceCounter,
} from './utils/serialization-utils';

import {registerKeyDeletionOnLogout} from './utils/serialization-handle-logout';
import {serializationConfig} from './utils//serialization-config';
import {initLocalforageKey, prepDataForStorage} from './utils/data-storage-utils-serialization';

/************************************** THIRD-PARTY MODULES ***************************************/
import {observe, action} from 'mobx';
import localforage from 'localforage';
import {javelinDataStore} from '@canimmunize/javelinscript';
import {get} from 'mad-utils/lib/shared';

/**************************************** TYPE DEFINITIONS ****************************************/
interface UnderlyingData {
    type: Function & {name: string};
    idField?: string;
    deleteEventName?: string;
}

interface SerializationCommon {
    isSingleton?: boolean;
    storePrefix?: string;
    underlyingData?: UnderlyingData;
}

interface SerializableClassConfig extends SerializationCommon {
    filename?: string;
}

interface StashValAtRefPropForInstanceArgs {
    instance: object;
    isSingleton: boolean;
}

interface InitSerializationArgs<T> extends SerializationCommon {
    instance: T;
}

/****************************** EXPORT SERIALIZABLE CLASS DECORATOR *******************************/
/**
 * {Class decorator}
 *
 * Mark a class for storage. Accepts object for configuring class' data's storage properties.
 * Builds the actual wrapping function/object constructor that runs code after the wrapped
 * object's constructor.
 *
 * Example: @Serializable({
 *              filename,
 *              isSingleton: true,
 *          })
 *          export class HomeScreenUIStore {...}
 *
 * @param {object} config Configuration for the initialization process (see above)
 *
 * @decorator Serializable
 */
export const Serializable = function SerializableDecoratorFactory(config: SerializableClassConfig) {
    /**
     * Get @Serializable config vals (or defaults)
     */
    const {isSingleton, storePrefix, filename} = getSerializableConfigValDefaults(config);
    const {underlyingData} = config;

    /* @return Actual decorator intended for the class itself */
    return function SerializableDecorator<TFunc extends Function>(Target: TFunc): TFunc {
        const fn = getSerializableDecTag<TFunc>(filename, Target);

        /**
         * Replacement constructor (monkey patches original)
         * @constructor
         */
        const WrappedStore = function(...args: any[]) {
            // Increase counter holding # of stores currently being initialized
            modelInitStatusStore().incrementNumLoadingStores();

            // Protect against async-await transpilation unbinding 'this'
            const self = this;

            // Run overridden constructor function
            const origRes = Target.apply(self, args);

            // Initialize properties / data
            initSerializable({
                instance: self,
                storePrefix,
                isSingleton,
                underlyingData,
            })
                .then(
                    action(obsDiscarders => {
                        // Reduce counter holding # of "loading" stores by 1 (this one is done)
                        modelInitStatusStore().decrementNumLoadingStores();
                    })
                )
                .catch(err => console.error(`${fn} initSerializable failed to initialize:`, err));
        };

        // Build orig prototype, bind it to the new constructor, & return the new constructor.
        return augmentNewConstructorWithOrig<TFunc>(Target, WrappedStore);
    };
};

/*********************************** SERIALIZATION INITIALIZER ************************************/
/**
 * Start initialization sequence for storing data in current class
 *
 * Set store instance up to sync all values with corresponding ones in localStorage
 * Set to store number of instances of the class, & increment the number on
 * each instantiation
 *
 * @param {Object} instance Instance of a class
 * @param {string} storePrefix Prefix added to stored values of store
 */
async function initSerializable<T extends object>({
    instance,
    storePrefix = ``,
    isSingleton = true,
    underlyingData,
}: InitSerializationArgs<T>) {
    // Don't store if current obj (instance) isn't init for storage or singleton
    //
    if (!isInitForStorage(instance, isSingleton)) {
        return;
    }

    // Set each serializable property up to store its value on change.
    addSetters(instance);

    // Store # of instances of current class on class instance
    // increment by 1 for this instance
    incrementClassInstanceCounter(instance, isSingleton); // TODO reduce this when deleting

    // Set reference prop to REF_PROP_VALUES list
    stashValAtRefPropForInstance({instance, isSingleton});

    // Setup to delete keys on logout
    registerKeyDeletionOnLogout(instance, storePrefix);

    // Setup handler to erase all localForage content if underlying data obj deleted
    registerDeleteHandler({
        instance,
        storePrefix,
        isSingleton,
        underlyingData,
    });

    // Set each serializable property up to store its value on change.
    return await syncStoreWithSerializableProps(instance, storePrefix);
}

/**
 * @param {object} instance Constructed instance of wrapped class type
 */
function addSetters(instance: object) {
    getStoredPropsList(instance).forEach(dataKey => {
        const setterKey = `set${dataKey.capitalize}`;
        if (!instance[setterKey]) instance[setterKey] = action(val => (instance[dataKey] = val));
    });
}

/**
 * Set data on reference prop values list
 * ONLY RELEVANT TO NON-SINGLETON INSTANCES
 *
 * @param {object} instance Class instance
 * @param {boolean} isSingleton If false, add to list of values at
 *                              referenceProp from each instance
 */
function stashValAtRefPropForInstance({instance, isSingleton}: StashValAtRefPropForInstanceArgs) {
    const fn = `stashValAtRefPropForInstance :: `;

    if (!isSingleton) {
        const refProp = getReferenceProp(instance);

        // Add new value at reference property to refPropValues if present
        if (refProp) {
            addInstanceRefPropValToRefPropValList(instance, instance[refProp]);
        }
    }
}

/**
 * If a decorated store's underlying data source (if defined) gets erased, remove all stored
 * props from localforage
 */
function registerDeleteHandler<T extends object>({
    instance,
    storePrefix = ``,
    isSingleton = true,
    underlyingData,
}: InitSerializationArgs<T>) {
    // Don't register ev_DELETE_RECORD handler for singletons or if no
    // 'underlying data' is defined
    if (
        instance &&
        underlyingData &&
        underlyingData.type &&
        underlyingData.idField &&
        !isSingleton
    ) {
        const deleteEventName = buildDeleteEventName(underlyingData);

        javelinDataStore.jvlnsBroadcaster.subscribe(deleteEventName, data => {
            /* Ensure the correct type is being built (return otherwise) */
            if (data.val.constructor !== underlyingData.type) return;

            /* Ensure this record matches the underlying data (return otherwise) */
            const instanceIdVal = instance[underlyingData.idField];
            const curDataVal = data.val[underlyingData.idField];
            if (!instanceIdVal || !curDataVal || instanceIdVal !== curDataVal) return;

            // Dispose of the listeners on the record (if any)
            get(instance, `disposers`, []).forEach(disposer => disposer());

            /* Erase all matches in localforage */
            try {
                return Promise.all(
                    getStoredPropsList(instance).map(prop =>
                        localforage.removeItem(
                            buildDbStorageKey(instance, prop, storePrefix, instanceIdVal)
                        )
                    )
                );
            } catch (err) {
                console.error(
                    `registerDeleteHandler -> ev_DELETE_RECORD: rm stored props failed w err:`,
                    err
                );
            }
        });
    }
}

/**
 * Set given class prototype up to store data as requested by property decorators.
 * For each serializable prop, attach an observer that writes to localForage on data change.
 *
 * @param {object} instance Bind observers to this instance's properties
 * @param {string} storePrefix Prefix to append to each stored value.
 */
async function syncStoreWithSerializableProps<T extends object>(
    instance: T,
    storePrefix: string = ``
) {
    const fn = `syncStoreWithSerializableProps :: `;

    // Attach change-synchronizing observer to each serializable prop (instanceKey)
    return await Promise.all(
        getStoredPropsList(instance).map(async (instanceKey: Extract<keyof T, string>) => {
            // Build prefix to store property with in storage
            const dbStorageKey = buildDbStorageKey(instance, instanceKey, storePrefix);

            try {
                // Initial sync betw data @ prop on instance & corresponding store data
                return await initLocalforageKey(instance, instanceKey, dbStorageKey)
                    // Watch val at instance->instanceKey for changes; store new data on change
                    .then(_out =>
                        observe(
                            instance,
                            instanceKey,
                            storeDataOnPropChange({
                                instance,
                                instanceKey,
                                dbStorageKey,
                            })
                        )
                    );
            } catch (error) {
                console.error(`${fn} initLocalforageKey failed with error:`, error);
            }
        })
    );
}

/**
 * When prop at {className}->{instanceKey} changes, store new val in
 * localForage at {dbStorageKey}
 *
 * @param {object} instance Instance of a data class
 * @param {string} instanceKey Key data originated from on data-containing instance
 * @param {string} dbStorageKey Key to store this data at in localforage
 */
function storeDataOnPropChange({
    instance,
    instanceKey,
    dbStorageKey,
}: {
    instance: object;
    instanceKey: string;
    dbStorageKey: string;
}) {
    const {className} = getClassData(instance);
    const fn = `storeDataOnPropChange (${className}) --> OBSERVE :::`;

    /**
     * Handler that stores value in localForage (IndexedDB) when triggered
     * @param {object} obs Observable object
     */
    return async (obs: {newValue: any}) => {
        /**
         *    INSTANCE -> STORE
         */
        try {
            // Store item in localForage virtual local DB (IndexedDB) & in
            // var for logging
            return await localforage
                .setItem(dbStorageKey, prepDataForStorage(instance, instanceKey, obs.newValue))
                .catch((err: Error) => console.error(`${fn}: data store failed. Error:`, err));
        } catch (err) {
            console.error(
                `${fn} [await] localforage.setItem / logSetStorageItem failed w error:`,
                err
            );
        }
    };
}

/******************************************** HELPERS *********************************************/
/**
 * Get config vals (or defaults) for @Serializable config object
 * @param {object} config Configuration object for initializing @Serializable decorator
 * @return {object} Object with values for isSingleton, storePrefix, filename
 */
function getSerializableConfigValDefaults(config: SerializableClassConfig) {
    return {
        isSingleton: config.isSingleton === true || false,
        storePrefix: config.storePrefix || ``,
        filename: config.filename ? `${config.filename} :: ` : ``,
    };
}

/**
 * Create display tag for logging in @Serializable decorator
 */
function getSerializableDecTag<TFunc extends Function>(filename: string, Target: TFunc): string {
    return `${filename}SerializableDecorator on ${Target.name || Target.constructor.name}`;
}

/**
 * Use the given underlying data object to construct a delete event name
 *
 * @example buildDeleteEventName({type: Record})
 */
function buildDeleteEventName(underlyingData: UnderlyingData): any {
    return (
        underlyingData.deleteEventName ||
        `ev_DELETE_${(
            underlyingData.type.name ||
            underlyingData.type.constructor.name ||
            'record'
        ).toUpperCase()}`
    );
}
